import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  ChevronRight,
  FolderKanban,
  MinusCircle,
  Users,
} from "lucide-react";
import { getSuperAdminDashboardStats } from "../../services/projectService";
import { formatPid } from "../../utils/formatPid";


const STATUS_STYLE = {
  active:   { bg: "#f0fdf4", color: "#166534", dot: "#22c55e" },
  inactive: { bg: "#f1f5f9", color: "#64748b", dot: "#94a3b8" },
};

/* ── Sub-components ───────────────────────────────────────────────────── */
const TrendBadge = ({ value, label }) => (
  <div
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      padding: "3px 8px",
      borderRadius: 999,
      background: value >= 0 ? "#f0fdf4" : "#fef2f2",
      color: value >= 0 ? "#166534" : "#dc2626",
      fontSize: 11,
      fontWeight: 700,
    }}
  >
    <ArrowUpRight
      size={12}
      style={{ transform: value < 0 ? "rotate(90deg)" : "none" }}
    />
    {Math.abs(value)}% {label}
  </div>
);

const StatCard = ({ icon: Icon, iconBg, iconColor, title, value, sub, trend, trendLabel }) => (
  <div style={{
    background: "#fff",
    border: "1px solid var(--borderColor)",
    borderRadius: 12,
    padding: "18px 20px",
    display: "flex", alignItems: "center", gap: 16,
    boxShadow: "0 1px 3px rgba(15,23,42,.05), 0 4px 12px rgba(15,23,42,.03)",
    width: "100%",
  }}>
    <div style={{
      width: 52, height: 52, borderRadius: 12, flexShrink: 0,
      background: iconBg, color: iconColor,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <Icon size={24} />
    </div>
    <div style={{ minWidth: 0, flex: 1 }}>
      <p style={{ margin: 0, fontSize: 12, color: "var(--muteColor)", fontWeight: 500, marginBottom: 2 }}>{title}</p>
      <h5 style={{
        margin: 0, fontWeight: 800, color: "#0f172a", lineHeight: 1.2, fontSize: 22,
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
      }}>{value}</h5>
      {trend !== undefined && (
        <div style={{ marginTop: 5 }}>
          <TrendBadge value={trend} label={trendLabel || "this month"} />
        </div>
      )}
      {sub && <p style={{ margin: "4px 0 0", fontSize: 11, color: "var(--muteColor)" }}>{sub}</p>}
    </div>
  </div>
);

/* ── Dashboard ───────────────────────────────────────────────────────── */
const SuperAdminDashboard = () => {
  const navigate = useNavigate();

  const [stats, setStats] = useState({
    totalProjects: 0, activeProjects: 0, inactiveProjects: 0, totalManagers: 0, recentProjects: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getSuperAdminDashboardStats()
      .then((res) => setStats((s) => ({ ...s, ...res.data?.response })))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const { totalProjects, activeProjects, inactiveProjects, totalManagers, recentProjects } = stats;

  return (
    <main className="app-wrapper url-list-page">
      <div className="container-fluid">

        {/* ── Stat cards ── */}
        <div className="db-stats-row">
          <StatCard
            icon={FolderKanban}
            iconBg="#ede9fe"
            iconColor="#7c3aed"
            title="Total Projects"
            value={loading ? "…" : totalProjects}
            sub={loading ? "" : `${activeProjects} active · ${inactiveProjects} inactive`}
          />
          <StatCard
            icon={CheckCircle2}
            iconBg="#f0fdf4"
            iconColor="#166534"
            title="Active Projects"
            value={loading ? "…" : activeProjects}
            sub="Currently running"
          />
          <StatCard
            icon={MinusCircle}
            iconBg="#f1f5f9"
            iconColor="#64748b"
            title="Inactive Projects"
            value={loading ? "…" : inactiveProjects}
            sub="Not in use"
          />
          <StatCard
            icon={Users}
            iconBg="#f0fdf4"
            iconColor="var(--successColor)"
            title="Total Managers"
            value={loading ? "…" : totalManagers}
            sub="Across all projects"
          />
        </div>

        {/* ── Bottom row ── */}
        <div className="db-tickets-row" style={{ alignItems: "flex-start" }}>

          {/* Recent Projects */}
          <div className="chart-card db-tickets-recent">
            <div className="card-head mb-1">
              <div className="d-flex align-items-center gap-2">
                <FolderKanban size={15} color="#7c3aed" />
                <h3>Recent Projects</h3>
              </div>
              <button className="db-ts-link-btn" onClick={() => navigate("/project-list")}>
                See all <ChevronRight size={13} />
              </button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", marginTop: 4 }}>
              {loading ? (
                <p style={{ fontSize: 13, color: "#94a3b8", padding: "16px 4px" }}>Loading…</p>
              ) : recentProjects.length === 0 ? (
                <p style={{ fontSize: 13, color: "#94a3b8", padding: "16px 4px" }}>No projects found.</p>
              ) : recentProjects.map((p, idx) => {
                const isActive = p.isActive === 1;
                const ss = isActive ? STATUS_STYLE.active : STATUS_STYLE.inactive;
                const initials = (p.projectName ?? "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
                const isLast = idx === recentProjects.length - 1;
                return (
                  <div
                    key={p.projectPid ?? idx}
                    onClick={() => navigate(`/project-details/${p.projectPid}`)}
                    style={{
                      display: "flex", alignItems: "center", gap: 12,
                      padding: "11px 4px",
                      borderBottom: isLast ? "none" : "1px solid #f1f5f9",
                      cursor: "pointer", transition: "background 0.15s", borderRadius: 6,
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = "#fafbfc"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  >
                    <div style={{ width: 34, height: 34, borderRadius: 9, flexShrink: 0, background: "var(--primaryTint)", color: "var(--primaryColor)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800 }}>
                      {initials}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#0f172a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.projectName ?? "—"}</p>
                      <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 2 }}>
                        <span style={{ fontSize: 11, color: "#94a3b8" }}>{formatPid(p.projectPid)}</span>
                      </div>
                    </div>
                    <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600, color: ss.color, flexShrink: 0 }}>
                      <span style={{ width: 7, height: 7, borderRadius: "50%", background: ss.dot, display: "inline-block" }} />
                      {isActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Projects overview */}
          <div className="chart-card db-tickets-overview">
            <div className="card-head mb-0">
              <div className="d-flex align-items-center gap-2">
                <FolderKanban size={16} color="#7c3aed" />
                <h3>Projects</h3>
              </div>
            </div>

            <div className="db-ts-total-row">
              <span className="db-ts-total-num">{loading ? "…" : totalProjects}</span>
              <span className="db-ts-total-label">Total Projects</span>
            </div>

            <div className="db-ts-stats">
              {[
                { label: "Active",   count: activeProjects,   dot: "#22c55e",             pct: totalProjects ? Math.round((activeProjects   / totalProjects) * 100) : 0 },
                { label: "Inactive", count: inactiveProjects, dot: "#94a3b8",             pct: totalProjects ? Math.round((inactiveProjects / totalProjects) * 100) : 0 },
                { label: "Managers", count: totalManagers,    dot: "var(--primaryColor)", pct: Math.min(100, totalManagers ? Math.round((totalManagers / Math.max(totalManagers, 1)) * 100) : 0) },
              ].map((s) => (
                <div className="db-ts-stat-row" key={s.label}>
                  <span className="db-ts-dot" style={{ background: s.dot }} />
                  <span className="db-ts-stat-label">{s.label}</span>
                  <div className="db-ts-bar-wrap">
                    <div className="db-ts-bar-fill" style={{ width: `${s.pct}%`, background: s.dot }} />
                  </div>
                  <span className="db-ts-count">{s.count}</span>
                </div>
              ))}
            </div>

            <button className="db-ts-view-btn" onClick={() => navigate("/project-list")}>
              View All Projects <ArrowRight size={14} />
            </button>
          </div>

        </div>
      </div>
    </main>
  );
};

export default SuperAdminDashboard;
